import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import tinycolor from 'tinycolor2';
import {Slider} from '@mui/material';
import Draggable from 'react-draggable';
import {darken, lighten, styled} from '@mui/material/styles';
import Box from '../../../Common/Box';
import utils from '../../utils';
import uiStore from "../../stores/uiStore";

const scale = uiStore.isMobile ? 0.5 : 1;

const ColorSlider = styled(Slider)({
    boxShadow: "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
    background: `linear-gradient(to right, #FF0000 ,#FFFF00 ,#00FF00, #00FFFF,#0000FF, #FF00FF, #FF0000)`,
    borderRadius: 0,
    height: `${35 * scale}px`,
    padding: 0,
    zIndex: 11,
    '& .MuiSlider-track': {
        border: 'none',
        borderRadius: 0,
        display: "none",
    },
    '& .MuiSlider-thumb': {
        color: "#787cff",
        boxShadow: "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
        height: 24,
        width: 24,
        backgroundColor: '#fff',
        border: '2px solid',
        ' &:hover': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
});

function formatSliderValue(value: number) {
    const v = value / 100;
    let r, g, b;
    if (v < 1 / 6) {
        r = 255;
        g = v / (1 / 6) * 255;
        b = 0;
    } else if (v < 2 / 6) {
        r = 255 - (v - 1 / 6) / (1 / 6) * 255;
        g = 255;
        b = 0;
    } else if (v < 3 / 6) {
        r = 0
        g = 255
        b = (v - 2 / 6) / (1 / 6) * 255;
    } else if (v < 4 / 6) {
        r = 0;
        g = 255 - (v - 3 / 6) / (1 / 6) * 255;
        b = 255;
    } else if (v < 5 / 6) {
        r = (v - 4 / 6) / (1 / 6) * 255;
        g = 0;
        b = 255;
    } else if (v < 6 / 6) {
        r = 255;
        g = 0;
        b = 255 - (v - 5 / 6) / (1 / 6) * 255;
    } else {
        r = 255;
        g = 0;
        b = 0;
    }

    return {r, g, b}
}

function toRgb(color: { r: number, g: number, b: number }) {
    const {r, g, b} = color;
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

interface ColorViewProps {
    onColorUpdate: (color: string) => void;
    defaultColor: string;
}

function ColorView(
        {
            onColorUpdate,
            defaultColor
        }: ColorViewProps
) {

    const draggableRef = React.useRef(null);
    const gradientViewRef = useRef();

    let clientWidth: number, clientHeight: number;
    let l = 0.5;
    let d = 0.5;

    // if (gradientViewRef.current) {
    //
    // }

    const {h, s, v} = tinycolor(defaultColor).toHsv();
    const defaultValue = h / 365 * 100;
    // const defaultColorPosition = {x: -(s - 0.5) * clientWidth, y: -(v - 0.5) * clientHeight};

    const [sliderColor, setSliderColor] = useState(formatSliderValue(defaultValue));
    const [sliderValue, setSliderValue] = useState(defaultValue);
    const [colorPosition, setColorPosition] = useState({x: 0, y: 0});
    const [dragging, setDragging] = useState(false);

    if (gradientViewRef.current) {
        ({clientWidth, clientHeight} = gradientViewRef.current);
        l = Math.min(1, 0.5 - colorPosition.x / clientWidth);
        d = Math.min(1, 0.5 + colorPosition.y / clientHeight);
    }

    const formattedSliderColor = toRgb(sliderColor);
    const bgcolor = darken(lighten(formattedSliderColor, l), d);

    useEffect(() => {
        if (gradientViewRef.current) {
            ({clientWidth, clientHeight} = gradientViewRef.current);

            if (clientWidth) {
                const sliderValue = Math.min(100, Math.ceil(h / 365 * 100));
                setSliderValue(sliderValue);
                setSliderColor(formatSliderValue(sliderValue));
                setColorPosition({x: (s - 0.5) * clientWidth - 1, y: -(v - 0.5) * clientHeight});
            }
        }


    }, [defaultColor, gradientViewRef.current]);


    const onPointerUp = (event: React.MouseEvent<HTMLElement> | React.TouchEvent) => {
        if (!dragging) {
            const rect = event.currentTarget.getBoundingClientRect();
            let clientX, clientY;


            if ((event as React.TouchEvent).changedTouches) {
                const touchEvent = (event as React.TouchEvent);
                clientX = touchEvent.changedTouches[0].clientX;
                clientY = touchEvent.changedTouches[0].clientY;
            } else {
                clientX = (event as React.MouseEvent).clientX;
                clientY = (event as React.MouseEvent).clientY;
            }

            const x = clientX - rect.left; //x position within the element.
            const y = clientY - rect.top;
            const position = {
                x: x - 0.5 * clientWidth - 13,
                y: y - 0.5 * clientHeight - 13,
            }
            setColorPosition(position);
        }
    }

    const onPointerEnd = () => onColorUpdate(bgcolor);

    return <Box
            id={"ams"}
            height={`${100}px`}
            width={"100%"}
            column
    >
        <Box row center padding={`${10 * scale}px`} bgcolor={bgcolor}>

        </Box>
        <ColorSlider
                sx={{
                    '& .MuiSlider-valueLabel': {
                        lineHeight: 1.2,
                        fontSize: 12,
                        background: 'unset',
                        padding: 0,
                        width: 32,
                        // height: 32 * scale,
                        height: 32,
                        borderRadius: '50% 50% 50% 0',
                        // backgroundColor: '#52af77',
                        transformOrigin: 'bottom left',
                        transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
                        '&:before': {display: 'none'},
                        '&.MuiSlider-valueLabelOpen': {
                            transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
                        },
                        '& > *': {
                            transform: 'rotate(45deg)',
                        },
                        backgroundColor: formattedSliderColor,
                    }
                }}
                valueLabelFormat={() => bgcolor}
                valueLabelDisplay="auto"
                aria-label="color slider"
                value={sliderValue}
                onChange={(e: Event) => {
                    // @ts-ignore
                    const sliderColor = formatSliderValue(e.target.value);
                    setSliderColor(sliderColor);
                    // @ts-ignore
                    setSliderValue(e.target.value);
                }}
                onMouseUp={onPointerEnd}
                onTouchEnd={onPointerEnd}
        />
        <Box
                overflow={"hidden"}
                row center
                forwardedRef={gradientViewRef}
                position={"relative"}
                flex={1}
                width={"100%"}
                style={{}}
        ><Box
                expand
                position={"absolute"}
                style={{
                    background: `linear-gradient(to right, #FFFFFF , ${formattedSliderColor})`
                }}
        />
            <Box
                    expand
                    position={"absolute"}
                    style={{
                        background: `linear-gradient(to bottom, rgba(0,0,0,0) , #000000)`
                    }}
            />
            <Box
                    onMouseUp={onPointerUp}
                    onTouchEnd={onPointerUp}
                    row
                    center id={"colorbox"}
                    position={"absolute"}
                    bottom={"-12px"}
                    top={"-12px"}
                    left={"-12px"}
                    right={"-12px"}>
                <Draggable
                        nodeRef={draggableRef}
                        defaultPosition={colorPosition}
                        position={colorPosition}
                        scale={1}
                        bounds={"#colorbox"}
                        onStart={() => setDragging(true)}
                        onDrag={(event, colorPosition) => {
                            setColorPosition(colorPosition);
                        }}
                        onStop={() => {
                            utils.sleep(200).then(() => {
                                setDragging(false);
                                onColorUpdate(bgcolor);
                            })
                        }}
                >
                    <Box
                            ref={draggableRef}
                            position={"absolute"}
                            margin={"auto"}
                            border={"1px solid white"}
                            width={`${25 * scale}px`}
                            height={`${25 * scale}px`}/>
                </Draggable>
            </Box>
        </Box></Box>
}


export default observer(ColorView);

